import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setInitial } from "../../store/slices/chatSlice";
import { useLang } from "../../context/LanguageContext";
import { toastError, toastSuccess } from "../../components/ToastNotification";
import MasterPromptService from "../../services/master-prompt";

const THUMB_MIN_H = 52;

const TRANSLATIONS = {
  EN: {
    title: "ORG AI RULES",
    labelSystemPrompt: "SYSTEM PROMPT TEXT",
    placeholder: "Enter system prompt text...",
    save: "SAVE",
    toastSaved: "Prompt updated successfully!",
    toastDeleted: "Prompt deleted successfully!",
  },
  ES: {
    title: "REGLAS DE IA ORG",
    labelSystemPrompt: "TEXTO DE INDICACIÓN DEL SISTEMA",
    placeholder: "Ingresa el texto de indicación del sistema...",
    save: "GUARDAR",
    toastSaved: "¡Prompt actualizado exitosamente!",
    toastDeleted: "¡Prompt eliminado exitosamente!",
  },
};

const AiRules = () => {
  const dispatch = useDispatch();
  const { lang } = useLang();
  const t = TRANSLATIONS[lang];

  const [systemPrompt, setSystemPrompt] = useState("");
  const [promptId, setPromptId]         = useState(null);
  const [saving, setSaving]             = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [loading, setLoading]           = useState(true);

  const [thumbTop, setThumbTop]       = useState(0);
  const [thumbH, setThumbH]           = useState(THUMB_MIN_H);
  const [hasOverflow, setHasOverflow] = useState(false);

  const textareaRef = useRef(null);
  const trackRef    = useRef(null);
  const thumbRef    = useRef(null);
  const thumbHRef   = useRef(THUMB_MIN_H);
  const thumbTopRef = useRef(0);
  const rafRef      = useRef(null);
  const dragRef     = useRef(null);

  // ── scrollbar calc ──────────────────────────────────────────────────────────
  const recalc = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const el    = textareaRef.current;
      const track = trackRef.current;
      if (!el || !track) return;

      const { scrollTop, scrollHeight, clientHeight } = el;
      const trackH   = track.clientHeight;
      const overflow = scrollHeight > clientHeight;

      setHasOverflow(overflow);
      if (!overflow) return;

      const h      = Math.max(THUMB_MIN_H, (clientHeight / scrollHeight) * trackH);
      const maxTop = trackH - h;
      const top    = scrollHeight === clientHeight ? 0
        : (scrollTop / (scrollHeight - clientHeight)) * maxTop;

      thumbHRef.current   = h;
      thumbTopRef.current = top;
      setThumbH(h);
      setThumbTop(top);
    });
  }, []);

  // ── scroll to bottom helper ─────────────────────────────────────────────────
  const scrollToBottom = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    recalc();
  }, [recalc]);

  // ── wire listeners ──────────────────────────────────────────────────────────
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    // always start at the bottom when this component mounts / re-mounts
    const goBottom = () => {
      el.scrollTop = el.scrollHeight;
      recalc();
    };

    // immediate attempt
    goBottom();

    const ro = new ResizeObserver(() => {
      // after any layout/size change (e.g. after sidebar tab switch re-renders)
      el.scrollTop = el.scrollHeight;
      recalc();
    });
    ro.observe(el);
    if (trackRef.current) ro.observe(trackRef.current);

    el.addEventListener("scroll", recalc);

    // when user switches browser tab and comes back
    const onVisibility = () => {
      if (!document.hidden) {
        el.scrollTop = el.scrollHeight;
        recalc();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    recalc();

    return () => {
      el.removeEventListener("scroll", recalc);
      document.removeEventListener("visibilitychange", onVisibility);
      ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [recalc, loading]);

  // recalc + scroll to bottom when text content changes
  useEffect(() => {
    scrollToBottom();
    const t1 = setTimeout(scrollToBottom, 60);
    const t2 = setTimeout(scrollToBottom, 250);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [systemPrompt, scrollToBottom]);

  // ── drag logic ──────────────────────────────────────────────────────────────
  const beginDrag = useCallback((startY, startTop) => {
    const onMove = (e) => {
      const el    = textareaRef.current;
      const track = trackRef.current;
      if (!el || !track) return;
      const maxTop = track.clientHeight - thumbHRef.current;
      const newTop = Math.min(Math.max(0, startTop + e.clientY - startY), maxTop);
      el.scrollTop = maxTop > 0
        ? (newTop / maxTop) * (el.scrollHeight - el.clientHeight) : 0;
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup",   onUp);
      dragRef.current = null;
    };
    dragRef.current = { onMove, onUp };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup",   onUp);
  }, []);

  const onThumbPointerDown = useCallback((e) => {
    e.preventDefault();
    beginDrag(e.clientY, thumbTopRef.current);
  }, [beginDrag]);

  const onTrackPointerDown = useCallback((e) => {
    if (thumbRef.current?.contains(e.target)) return;
    e.preventDefault();
    const el    = textareaRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const rect   = track.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const maxTop = track.clientHeight - thumbHRef.current;
    const newTop = Math.min(Math.max(0, clickY - thumbHRef.current / 2), maxTop);
    el.scrollTop = maxTop > 0
      ? (newTop / maxTop) * (el.scrollHeight - el.clientHeight) : 0;
    beginDrag(e.clientY, newTop);
  }, [beginDrag]);

  // ── data ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(setInitial(true));
    fetchPrompt();
  }, []);

  const fetchPrompt = async () => {
    try {
      const res  = await MasterPromptService.getAll();
      const list = res?.data?.data;
      if (Array.isArray(list) && list.length > 0) {
        setPromptId(list[0]._id);
        setSystemPrompt(list[0].master_prompt ?? "");
      }
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (promptId) {
        await MasterPromptService.update(promptId, { master_prompt: systemPrompt });
      } else {
        const res = await MasterPromptService.create({ master_prompt: systemPrompt });
        const created = res?.data?.data;
        if (created?._id) setPromptId(created._id);
      }
      toastSuccess(t.toastSaved);
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!promptId) return;
    setDeleting(true);
    try {
      await MasterPromptService.remove(promptId);
      setPromptId(null);
      setSystemPrompt("");
      toastSuccess(t.toastDeleted);
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setDeleting(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto p-4 lg:p-6 gap-5">

        {/* Header */}
        <div className="flex items-center flex-shrink-0">
          <h1 className="font-roboto font-bold text-xl lg:text-2xl text-primary uppercase tracking-wide">
            {t.title}
          </h1>
        </div>

        {/* Card */}
        <div className="flex flex-col flex-1 min-h-0 bg-white rounded-2xl border border-primary/10 shadow-sm overflow-hidden">

          {/* Card header */}
          {/* <div className="flex items-center justify-between px-5 py-3 border-b border-primary/10 flex-shrink-0">
            <span className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {t.labelSystemPrompt}
            </span>
            {promptId && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="p-1.5 rounded-lg bg-red-50 text-red-400 hover:bg-red-500 hover:text-white transition-all disabled:opacity-60"
              >
                {deleting
                  ? <div className="loader !w-4 !h-4" />
                  : <MdDeleteOutline className="text-lg" />}
              </button>
            )}
          </div> */}

          {loading ? (
            <div className="flex-1 min-h-0 bg-[#F3F1F9] rounded-b-2xl p-6 flex flex-col gap-3.5 select-none">
              <div className="w-3/5 h-4 rounded-md skeleton-bone-primary" />
              <div className="w-full h-4 rounded-md skeleton-bone" />
              <div className="w-11/12 h-4 rounded-md skeleton-bone" />
              <div className="w-4/5 h-4 rounded-md skeleton-bone" />
              <div className="w-2/3 h-4 rounded-md skeleton-bone" />
              <div className="w-3/4 h-4 rounded-md skeleton-bone" />
              <div className="w-1/2 h-4 rounded-md skeleton-bone" />
            </div>
          ) : (
            <div className="flex gap-[10.18px] flex-1 min-h-0 bg-[#F3F1F9] rounded-b-2xl p-3">
              <textarea
                ref={textareaRef}
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder={t.placeholder}
                className="flex-1 min-h-0 w-full resize-none bg-transparent px-2 py-2 font-roboto text-sm text-gray-700 placeholder-gray-400 focus:outline-none overflow-y-scroll no-scrollbar"
              />

              {/* custom scrollbar track */}
              <div
                ref={trackRef}
                className="relative w-[14px] lg:w-[17.94px] self-stretch bg-[#C7BFE4] rounded-[17px] flex-shrink-0 cursor-pointer"
                style={{ visibility: hasOverflow ? "visible" : "hidden" }}
                onPointerDown={onTrackPointerDown}
              >
                <div
                  ref={thumbRef}
                  className="absolute w-full bg-[#3D2B90] rounded-[10px] cursor-grab active:cursor-grabbing"
                  style={{ top: `${thumbTop}px`, height: `${thumbH}px` }}
                  onPointerDown={onThumbPointerDown}
                />
              </div>
            </div>
          )}
        </div>

        {/* Save */}
        <div className="flex justify-end flex-shrink-0">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-10 py-2.5 rounded-xl font-roboto font-bold text-sm text-white bg-primary hover:bg-primary/90 transition-all uppercase tracking-wide disabled:opacity-60 min-w-[120px] flex items-center justify-center"
          >
            {saving ? <div className="loader" /> : t.save}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AiRules;
